import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, TextField, Table, TableBody, TableCell, 
  TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, 
  DialogActions, Select, MenuItem, FormControl, InputLabel, IconButton,
  Chip, InputAdornment, Autocomplete, Grow, Grid
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';

function References() {
  const [references, setReferences] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Form State
  const [formData, setFormData] = useState({
    type: 'Out',
    project_id: '',
    subject: '',
    recipient_to: '',
    address: '',
    description: '',
    status: 'Pending'
  });

  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('user_id');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchReferences = async () => {
    setLoading(true);
    try {
      const params = { user_id: userId };
      if (search) params.search = search;
      
      const res = await axios.get(`${API_URL}/references`, { headers, params });
      setReferences(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API_URL}/projects`, { headers, params: { user_id: userId } });
      setProjects(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching projects", err);
    }
  };

  useEffect(() => {
    fetchReferences();
    fetchProjects();
  }, [search]);

  const handleCreate = async () => {
    if (!formData.subject || !formData.type) {
        alert("Please fill in required fields (Type, Subject)");
        return;
    }

    setCreateLoading(true);
    try {
      await axios.post(`${API_URL}/references`, {
        user_id: userId,
        ...formData
      }, { headers });
      
      setOpen(false);
      setFormData({
        type: 'Out',
        project_id: '',
        subject: '',
        recipient_to: '',
        address: '',
        description: '',
        status: 'Pending'
      });
      fetchReferences();
    } catch (err) {
      console.error(err);
      alert("Error creating reference");
    } finally {
      setCreateLoading(false);
    }
  };

  const getStatusColor = (status) => {
      switch(status) {
          case 'Approved': return 'success';
          case 'Pending': return 'warning';
          case 'Rejected': return 'error';
          default: return 'default';
      }
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      {/* Modern Gradient Header */}
      <Grow in={true} timeout={500}>
        <Box sx={{ 
            mb: 4, 
            mt: 2, 
            p: 4, 
            borderRadius: 4, 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
            color: 'white',
            boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 3
        }}>
        {/* Background decorative elements */}
        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', zIndex: 1 }} />
        <Box sx={{ position: 'absolute', bottom: -20, right: 100, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', zIndex: 1 }} />

        <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                <ReceiptLongIcon sx={{ fontSize: 36, color: '#90caf9' }} />
                References
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Manage and track all internal and external references
            </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, width: { xs: '100%', md: 'auto' }, position: 'relative', zIndex: 2 }}>
            <TextField 
                size="small" 
                placeholder="Search references..." 
                value={search} 
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                    startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                }}
                sx={{ 
                    bgcolor: 'white', 
                    flexGrow: 1,
                    minWidth: { xs: '100%', sm: 300 },
                    borderRadius: 2,
                    '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        '& fieldset': { borderColor: 'transparent' },
                        '&:hover fieldset': { borderColor: 'transparent' },
                        '&.Mui-focused fieldset': { borderColor: 'transparent' }
                    }
                }}
            />
            <Button 
                variant="contained" 
                startIcon={<AddIcon />} 
                onClick={() => setOpen(true)}
                sx={{ 
                    borderRadius: 2, 
                    px: 3, 
                    textTransform: 'none', 
                    fontWeight: 'bold',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    bgcolor: 'white',
                    color: '#1e3c72',
                    '&:hover': {
                        bgcolor: '#f5f5f5'
                    }
                }}
            >
                New Reference
            </Button>
        </Box>
        </Box>
      </Grow>

      {/* Main Content */}
      <Grow in={true} timeout={800}>
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <Box sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>Ref Number</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>Project</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>Subject</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>To</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>Date</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
               <TableRow>
                 <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                   <Loading />
                 </TableCell>
               </TableRow>
            ) : references.length > 0 ? (
              references.map((ref) => (
                <TableRow key={ref.id} hover sx={{ '&:last-child td': { border: 0 }, transition: 'background-color 0.2s' }}>
                  <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 600, color: 'primary.main', bgcolor: 'primary.50', display: 'inline-block', px: 1, py: 0.5, borderRadius: 1 }}>
                          {ref.ref_number}
                      </Typography>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{ref.project_name || <Typography variant="body2" color="text.disabled">None</Typography>}</TableCell>
                  <TableCell>
                      <Chip 
                          label={ref.type} 
                          size="small" 
                          sx={{ 
                              fontWeight: 600, 
                              bgcolor: ref.type === 'In' ? '#e0f2fe' : ref.type === 'Out' ? '#f3e8ff' : '#f1f5f9',
                              color: ref.type === 'In' ? '#0ea5e9' : ref.type === 'Out' ? '#a855f7' : '#64748b',
                              borderRadius: 1.5
                          }} 
                      />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 250 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500 }}>
                          {ref.subject}
                      </Typography>
                  </TableCell>
                  <TableCell>
                      <Typography variant="body2" color="text.secondary">
                          {ref.recipient_to || '-'}
                      </Typography>
                  </TableCell>
                  <TableCell>
                      <Chip 
                          label={ref.status} 
                          size="small" 
                          color={getStatusColor(ref.status)} 
                          sx={{ fontWeight: 600, borderRadius: 1.5 }} 
                      />
                  </TableCell>
                  <TableCell>
                      <Typography variant="body2" color="text.secondary">
                          {ref.created_at ? new Date(ref.created_at).toLocaleDateString() : '-'}
                      </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                        onClick={() => navigate(`/references/${ref.id}`)} 
                        size="small"
                        sx={{ 
                            color: 'primary.main',
                            bgcolor: 'primary.50',
                            '&:hover': { bgcolor: 'primary.100' }
                        }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'text.secondary' }}>
                      <ReceiptLongIcon sx={{ fontSize: 48, color: 'divider', mb: 2 }} />
                      <Typography variant="h6" color="text.primary" gutterBottom>No References Found</Typography>
                      <Typography variant="body2">Get started by creating a new reference.</Typography>
                      <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setOpen(true)} sx={{ mt: 3, borderRadius: 2 }}>
                          Create Reference
                      </Button>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </Box>
      </Paper>
      </Grow>

      {/* Create Dialog */}
      <Dialog 
        open={open} 
        onClose={() => setOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
            sx: { borderRadius: 4, overflow: 'hidden' }
        }}
      >
        <DialogTitle sx={{ bgcolor: '#1e3c72', color: 'white', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Record New Reference
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'white' }}>
                <CloseIcon />
            </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#f8fafc', borderTop: 0, borderBottom: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            
            {/* Classification */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Classification</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={formData.type}
                            label="Type"
                            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                          >
                            <MenuItem value="In">In</MenuItem>
                            <MenuItem value="Out">Out</MenuItem>
                            <MenuItem value="Certification">Certification</MenuItem>
                          </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel>Status</InputLabel>
                          <Select
                            value={formData.status}
                            label="Status"
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                          >
                            <MenuItem value="Pending">Pending</MenuItem>
                            <MenuItem value="Approved">Approved</MenuItem>
                            <MenuItem value="Rejected">Rejected</MenuItem>
                          </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Autocomplete
                            options={projects}
                            getOptionLabel={(option) => option.name || ""}
                            value={projects.find(p => p.id === formData.project_id) || null}
                            onChange={(event, newValue) => {
                                setFormData({ ...formData, project_id: newValue ? newValue.id : '' });
                            }}
                            renderInput={(params) => <TextField {...params} label="Project (Optional)" fullWidth variant="outlined" />}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Reference Details */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Reference Details</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                          label="Subject"
                          fullWidth
                          required
                          value={formData.subject}
                          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                          variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                          label="To (Recipient)"
                          fullWidth
                          value={formData.recipient_to}
                          onChange={(e) => setFormData({ ...formData, recipient_to: e.target.value })}
                          variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                          label="Address"
                          fullWidth
                          value={formData.address}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                          variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                          label="Description"
                          fullWidth
                          multiline
                          rows={3}
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          variant="outlined"
                        />
                    </Grid>
                </Grid>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setOpen(false)} sx={{ color: '#64748b', fontWeight: 'bold' }}>Cancel</Button>
          <Button 
            onClick={handleCreate} 
            variant="contained" 
            disabled={createLoading}
            sx={{ 
                borderRadius: 2, 
                fontWeight: 'bold', 
                px: 3, 
                py: 1,
                background: 'linear-gradient(45deg, #1e3c72 0%, #2a5298 100%)',
                boxShadow: '0 4px 12px rgba(30,60,114,0.3)'
            }}
          >
            {createLoading ? 'Generating...' : 'Generate New Reference'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default References;
